<template>
  <div class="trading-interface">
    <v-row>
      <!-- Gráfico de Preço -->
      <v-col cols="12" lg="8">
        <v-card height="700">
          <v-card-title class="d-flex justify-space-between align-center">
            <div class="d-flex align-center">
              <v-avatar size="28" color="primary" class="mr-2">
                <span class="text-h6 text-white">Q</span>
              </v-avatar>
              
              <v-select
                v-model="parAtual"
                :items="paresTradingDisponiveis"
                variant="plain"
                density="compact"
                hide-details
                class="trading-pair-select"
              ></v-select>
            </div>
            
            <div class="d-flex align-center">
              <v-btn-toggle
                v-model="intervaloTempo"
                mandatory
                density="comfortable"
                variant="outlined"
                class="mr-2"
              >
                <v-btn value="1h">1h</v-btn>
                <v-btn value="1d">1d</v-btn>
                <v-btn value="1w">1w</v-btn>
                <v-btn value="1m">1m</v-btn>
              </v-btn-toggle>
              
              <v-btn 
                icon="mdi-fullscreen"
                variant="text"
                density="comfortable"
                @click="maximizarGrafico"
              ></v-btn>
            </div>
          </v-card-title>
          
          <v-divider></v-divider>
          
          <!-- Mockup de Gráfico (em produção seria um gráfico real com Chart.js) -->
          <div class="price-chart pa-4 d-flex flex-column justify-center align-center">
            <div class="text-h6 mb-2">{{ parAtual }}</div>
            <div class="text-h4 font-weight-bold mb-3 d-flex align-center">
              {{ precoPar }}
              <span 
                :class="[
                  'ml-2 text-body-2 font-weight-medium',
                  variacaoPositiva ? 'text-success' : 'text-error'
                ]"
              >
                {{ variacaoPositiva ? '+' : '' }}{{ variacaoPar }}%
              </span>
            </div>
            
            <!-- Gráfico avançado com ferramentas de análise técnica -->
            <div class="advanced-chart-container">
              <div class="chart-toolbar d-flex justify-space-between align-center mb-2">
                <div class="chart-indicators">
                  <v-chip-group>
                    <v-chip size="small" color="primary" variant="outlined" @click="adicionarIndicador('ma')">MA</v-chip>
                    <v-chip size="small" color="primary" variant="outlined" @click="adicionarIndicador('ema')">EMA</v-chip>
                    <v-chip size="small" color="primary" variant="outlined" @click="adicionarIndicador('rsi')">RSI</v-chip>
                    <v-chip size="small" color="primary" variant="outlined" @click="adicionarIndicador('macd')">MACD</v-chip>
                    <v-chip size="small" color="primary" variant="outlined" @click="adicionarIndicador('bb')">Bollinger</v-chip>
                  </v-chip-group>
                </div>
                <div class="chart-tools">
                  <v-btn-group density="comfortable" variant="text">
                    <v-btn icon="mdi-magnify-plus-outline" @click="zoomIn" size="small"></v-btn>
                    <v-btn icon="mdi-magnify-minus-outline" @click="zoomOut" size="small"></v-btn>
                    <v-btn icon="mdi-ruler" @click="toggleFibonacci" size="small"></v-btn>
                    <v-btn icon="mdi-pencil" @click="toggleDrawing" size="small"></v-btn>
                  </v-btn-group>
                </div>
              </div>
              
              <!-- Simulação de gráfico de velas com indicadores -->
              <div class="candlestick-chart position-relative">
                <!-- Linhas de indicadores (simulação) -->
                <div v-if="indicadoresAtivos.includes('ma')" class="indicator-line ma-line"></div>
                <div v-if="indicadoresAtivos.includes('ema')" class="indicator-line ema-line"></div>
                <div v-if="indicadoresAtivos.includes('bb')" class="indicator-band bb-upper"></div>
                <div v-if="indicadoresAtivos.includes('bb')" class="indicator-band bb-lower"></div>
                <div v-if="fibonacciAtivo" class="fibonacci-overlay"></div>
                
                <!-- Candles -->
                <div 
                  v-for="i in 30" 
                  :key="i" 
                  class="candle"
                  :class="{ 'candle-up': true }"
                >
                  <div class="wick"></div>
                  <div class="body"></div>
                  <div class="wick bottom"></div>
                </div>
              </div>
              
              <!-- Volume na parte inferior -->
              <div class="volume-chart mt-2">
                <div 
                  v-for="i in 30" 
                  :key="i" 
                  class="volume-bar"
                  :class="{ 'volume-up': true }"
                  :style="{ height: `${25}px` }"
                ></div>
              </div>
            </div>
            
            <div class="d-flex justify-space-between w-100 mt-2">
              <span class="text-caption">{{ horaInicio }}</span>
              <span class="text-caption">Agora</span>
            </div>
          </div>
        </v-card>
      </v-col>
      
      <!-- Formulário de Trading -->
      <v-col cols="12" md="6" lg="4">
        <v-card>
          <v-tabs v-model="tabTrading">
            <v-tab value="comprar">{{ t('dex.buy') }}</v-tab>
            <v-tab value="vender">{{ t('dex.sell') }}</v-tab>
          </v-tabs>
          
          <v-card-text>
            <v-window v-model="tabTrading">
              <v-window-item value="comprar">
                <div class="d-flex justify-space-between align-center mb-4">
                  <div class="text-h6">Comprar {{ tokenCompra }}</div>
                  <div>
                    <span class="text-caption">Saldo: </span>
                    <span>{{ saldoTokenPagamento }}</span> 
                    <span>{{ tokenPagamento }}</span>
                  </div>
                </div>
                
                <v-text-field
                  v-model="valorCompra"
                  :label="t('dex.price')"
                  type="number"
                  min="0"
                  variant="outlined"
                  density="compact"
                  :suffix="tokenPagamento"
                  class="mb-2"
                ></v-text-field>
                
                <v-text-field
                  v-model="quantidadeCompra"
                  :label="t('dex.amount')"
                  type="number"
                  min="0"
                  variant="outlined"
                  density="compact"
                  :suffix="tokenCompra"
                  class="mb-2"
                ></v-text-field>
                
                <div class="d-flex justify-space-between align-center mb-4">
                  <span>{{ t('dex.total') }}</span>
                  <span class="text-subtitle-1 font-weight-bold">
                    {{ calcularTotal(valorCompra, quantidadeCompra) }} {{ tokenPagamento }}
                  </span>
                </div>
                
                <v-btn block color="success">Comprar {{ tokenCompra }}</v-btn>
              </v-window-item>
              
              <v-window-item value="vender">
                <div class="d-flex justify-space-between align-center mb-4">
                  <div class="text-h6">Vender {{ tokenCompra }}</div>
                  <div>
                    <span class="text-caption">Saldo: </span>
                    <span>{{ saldoTokenCompra }}</span> 
                    <span>{{ tokenCompra }}</span>
                  </div>
                </div>
                
                <v-text-field
                  v-model="valorVenda"
                  :label="t('dex.price')"
                  type="number"
                  min="0"
                  variant="outlined"
                  density="compact"
                  :suffix="tokenPagamento"
                  class="mb-2"
                ></v-text-field>
                
                <v-text-field
                  v-model="quantidadeVenda"
                  :label="t('dex.amount')"
                  type="number"
                  min="0"
                  variant="outlined"
                  density="compact"
                  :suffix="tokenCompra"
                  class="mb-2"
                ></v-text-field>
                
                <div class="d-flex justify-space-between align-center mb-4">
                  <span>{{ t('dex.total') }}</span>
                  <span class="text-subtitle-1 font-weight-bold">
                    {{ calcularTotal(valorVenda, quantidadeVenda) }} {{ tokenPagamento }}
                  </span>
                </div>
                
                <v-btn block color="error">Vender {{ tokenCompra }}</v-btn>
              </v-window-item>
            </v-window>
          </v-card-text>
        </v-card>
        
        <v-card class="mt-3">
          <v-card-title class="text-subtitle-1">
            <v-icon class="mr-2">mdi-wallet-outline</v-icon>
            Seus Saldos
          </v-card-title>
          
          <v-card-text>
            <v-list>
              <v-list-item v-for="token in tokens" :key="token.symbol">
                <template v-slot:prepend>
                  <v-avatar size="32" :color="token.color" class="mr-2">
                    <span class="text-white">{{ token.symbol.charAt(0) }}</span>
                  </v-avatar>
                </template>
                
                <v-list-item-title>{{ token.name }}</v-list-item-title>
                
                <template v-slot:append>
                  <span class="font-weight-medium">{{ token.saldo }}</span>
                </template>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    
    <!-- Segunda linha com livro de ordens e minhas ordens -->
    <v-row class="mt-4">
      <v-col cols="12" md="6">
        <!-- Minhas Ordens Pendentes -->
        <v-card class="mb-4">
          <v-card-title class="d-flex align-center justify-space-between">
            <div>
              <v-icon class="mr-2">mdi-clock-outline</v-icon>
              Minhas Ordens Pendentes
            </div>
            <v-chip
              color="primary"
              size="small"
              pill
              class="font-weight-medium"
            >{{ minhasOrdens.length }}</v-chip>
          </v-card-title>
          
          <v-divider></v-divider>
          
          <v-card-text class="pa-0" v-if="minhasOrdens.length > 0">
            <v-list>
              <v-list-item 
                v-for="(ordem, index) in minhasOrdens"
                :key="'ordem-'+index"
                :class="ordem.tipo === 'compra' ? 'bg-success-lighten-5' : 'bg-error-lighten-5'"
                class="mb-1"
              >
                <v-list-item-title class="d-flex justify-space-between align-center">
                  <div class="d-flex align-center">
                    <v-chip
                      :color="ordem.tipo === 'compra' ? 'success' : 'error'"
                      size="small"
                      class="mr-2"
                    >
                      {{ ordem.tipo === 'compra' ? 'Compra' : 'Venda' }}
                    </v-chip>
                    <span class="font-weight-medium">{{ ordem.quantidade }} {{ ordem.par.split('/')[0] }}</span>
                  </div>
                  
                  <div>
                    <span class="font-weight-medium">{{ formatarPreco(ordem.preco) }}</span>
                    <span class="text-caption"> {{ ordem.par.split('/')[1] }}</span>
                  </div>
                </v-list-item-title>
                
                <v-list-item-subtitle class="d-flex justify-space-between">
                  <span class="text-caption">{{ formatarData(ordem.data) }}</span>
                  <span class="text-caption">Total: {{ formatarPreco(ordem.preco * ordem.quantidade) }} {{ ordem.par.split('/')[1] }}</span>
                </v-list-item-subtitle>
                
                <template v-slot:append>
                  <v-btn
                    size="small"
                    color="error"
                    variant="text"
                    icon="mdi-close-circle"
                    @click="cancelarOrdem(ordem.id)"
                  ></v-btn>
                </template>
              </v-list-item>
            </v-list>
          </v-card-text>
          
          <v-card-text v-else class="text-center pa-4">
            <v-icon size="48" color="grey" class="mb-2">mdi-clock-outline</v-icon>
            <div class="text-subtitle-1 mb-1">Nenhuma ordem pendente</div>
            <div class="text-caption">Suas ordens de compra e venda em aberto aparecerão aqui</div>
          </v-card-text>
        </v-card>
      
        <!-- Livro de ordens -->
        <OrderBook 
          :pairSymbol="parAtual" 
          :baseToken="tokenCompra" 
          :quoteToken="tokenPagamento" 
        />
      </v-col>
      
      <v-col cols="12" md="6">
        <!-- Ordens avançadas -->
        <v-card>
          <v-card-title class="d-flex align-center">
            <v-icon class="mr-2">mdi-chart-timeline-variant</v-icon>
            Ordens Avançadas
          </v-card-title>
          
          <v-divider></v-divider>
          
          <v-card-text>
            <v-tabs v-model="tabOrdemAvancada">
              <v-tab value="stop">Stop-Loss</v-tab>
              <v-tab value="take">Take-Profit</v-tab>
              <v-tab value="oco">OCO</v-tab>
            </v-tabs>
            
            <v-window v-model="tabOrdemAvancada" class="mt-3">
              <!-- Stop Loss -->
              <v-window-item value="stop">
                <div class="text-subtitle-2 mb-2">Stop-Loss (Limitar Perda)</div>
                <p class="text-caption mb-4">Crie uma ordem que será executada automaticamente quando o preço atingir ou ficar abaixo de um valor especificado.</p>
                
                <v-text-field
                  v-model="stopLossPreco"
                  label="Preço de gatilho"
                  type="number"
                  min="0"
                  variant="outlined"
                  density="compact"
                  :suffix="tokenPagamento"
                  class="mb-2"
                  hint="Quando o preço atingir este valor, sua ordem será executada"
                  persistent-hint
                ></v-text-field>
                
                <v-text-field
                  v-model="stopLossQuantidade"
                  :label="t('dex.amount')"
                  type="number"
                  min="0"
                  variant="outlined"
                  density="compact"
                  :suffix="tokenCompra"
                  class="mb-4"
                ></v-text-field>
                
                <v-btn color="warning" block>Criar Ordem Stop-Loss</v-btn>
              </v-window-item>
              
              <!-- Take Profit -->
              <v-window-item value="take">
                <div class="text-subtitle-2 mb-2">Take-Profit (Garantir Lucro)</div>
                <p class="text-caption mb-4">Crie uma ordem que será executada automaticamente quando o preço atingir ou superar um valor especificado.</p>
                
                <v-text-field
                  v-model="takeProfitPreco"
                  label="Preço de gatilho"
                  type="number"
                  min="0"
                  variant="outlined"
                  density="compact"
                  :suffix="tokenPagamento"
                  class="mb-2"
                  hint="Quando o preço atingir este valor, sua ordem será executada"
                  persistent-hint
                ></v-text-field>
                
                <v-text-field
                  v-model="takeProfitQuantidade"
                  :label="t('dex.amount')"
                  type="number"
                  min="0"
                  variant="outlined"
                  density="compact"
                  :suffix="tokenCompra"
                  class="mb-4"
                ></v-text-field>
                
                <v-btn color="success" block>Criar Ordem Take-Profit</v-btn>
              </v-window-item>
              
              <!-- OCO (One Cancels Other) -->
              <v-window-item value="oco">
                <div class="text-subtitle-2 mb-2">OCO (One Cancels Other)</div>
                <p class="text-caption mb-4">Crie um par de ordens onde a execução de uma automaticamente cancela a outra. Combinação de Stop-Loss e Take-Profit.</p>
                
                <v-text-field
                  v-model="ocoStopPreco"
                  label="Preço Stop-Loss"
                  type="number"
                  min="0"
                  variant="outlined"
                  density="compact"
                  :suffix="tokenPagamento"
                  class="mb-2"
                  hint="Abaixo deste preço"
                  persistent-hint
                ></v-text-field>
                
                <v-text-field
                  v-model="ocoTakePreco"
                  label="Preço Take-Profit"
                  type="number"
                  min="0"
                  variant="outlined"
                  density="compact"
                  :suffix="tokenPagamento"
                  class="mb-2"
                  hint="Acima deste preço"
                  persistent-hint
                ></v-text-field>
                
                <v-text-field
                  v-model="ocoQuantidade"
                  :label="t('dex.amount')"
                  type="number"
                  min="0"
                  variant="outlined"
                  density="compact"
                  :suffix="tokenCompra"
                  class="mb-4"
                ></v-text-field>
                
                <v-btn color="primary" block>Criar Ordem OCO</v-btn>
              </v-window-item>
            </v-window>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import OrderBook from '@/components/dex/OrderBook.vue'
import { useAppStore } from '@/stores/app'

export default {
  name: 'TradingInterface',
  components: {
    OrderBook
  },
  data() {
    const c = window.__QUARNX_COIN__ || 'tKinX'
    return {
      paresTradingDisponiveis: [`${c}/QUSD`, `ETH/${c}`, `BNB/${c}`, `BTC/${c}`],
      parAtual: `${c}/QUSD`,
      intervaloTempo: '1d',
      
      // DEX em desenvolvimento - dados carregados da API quando disponível
      tokenCompra: c,
      tokenPagamento: 'QUSD',
      saldoTokenCompra: 0,
      saldoTokenPagamento: 0,
      
      // Tabs
      tabTrading: 'comprar',
      tabOrdemAvancada: 'stop',
      
      // Formulário de compra
      valorCompra: '',
      qtdCompra: '',
      
      // Formulário de venda
      valorVenda: '',
      qtdVenda: '',
      
      // Formulário de Stop-Loss
      stopLossPreco: '',
      stopLossQtd: '',
      
      // Formulário de Take-Profit
      takeProfitPreco: '',
      takeProfitQtd: '',
      
      // Formulário OCO
      ocoStopPreco: '',
      ocoTakePreco: '',
      ocoQtd: '',
      
      // Opções do gráfico avançado
      graficoMaximizado: false,
      indicadoresAtivos: [],
      nivelZoom: 1.0,
      fibonacciAtivo: false,
      modoDesenho: false,
      
      // Tokens em carteira (carregados da API)
      tokensCarteira: [],
      
      // Ordens pendentes (carregadas da API quando DEX estiver disponível)
      minhasOrdens: [],
    }
  },
  computed: {
    appStore() {
      return useAppStore()
    },
    dexEmDesenvolvimento() {
      return this.minhasOrdens.length === 0 && this.tokensCarteira.length === 0
    },
    precoPar() {
      return "1.25"
    },
    variacaoPar() {
      return "5.2"
    },
    variacaoPositiva() {
      return true
    },
    horaInicio() {
      switch(this.intervaloTempo) {
        case '1h':
          return this.formatarHora(this.minutosAtras(60))
        case '1d':
          return this.formatarHora(this.minutosAtras(60 * 24))
        case '1w':
          return this.formatarHora(this.minutosAtras(60 * 24 * 7))
        case '1m':
          return this.formatarHora(this.minutosAtras(60 * 24 * 30))
        default:
          return this.formatarHora(this.minutosAtras(60))
      }
    }
  },
  methods: {
    calcularTotal(valor, quantidade) {
      return (valor * quantidade).toFixed(4)
    },
    minutosAtras(minutos) {
      const data = new Date()
      data.setMinutes(data.getMinutes() - minutos)
      return data
    },
    formatarHora(data) {
      return data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    },
    formatarPreco(preco) {
      return preco.toFixed(4)
    },
    formatarData(data) {
      return data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
    },
    // Métodos para o gráfico avançado
    maximizarGrafico() {
      // Em uma implementação real, isso abriria um modal em tela cheia
      this.graficoMaximizado = !this.graficoMaximizado
      this.$nextTick(() => {
        // Código para reconstruir o gráfico no novo tamanho
        console.log('Gráfico redimensionado para', this.graficoMaximizado ? 'tela cheia' : 'normal')
      })
    },
    adicionarIndicador(indicador) {
      if (this.indicadoresAtivos.includes(indicador)) {
        // Remover indicador se já estiver ativado
        this.indicadoresAtivos = this.indicadoresAtivos.filter(i => i !== indicador)
      } else {
        // Adicionar indicador
        this.indicadoresAtivos.push(indicador)
      }
    },
    zoomIn() {
      this.nivelZoom = Math.min(this.nivelZoom + 0.1, 2.0)
      console.log('Zoom aumentado para', this.nivelZoom)
    },
    zoomOut() {
      this.nivelZoom = Math.max(this.nivelZoom - 0.1, 0.5)
      console.log('Zoom reduzido para', this.nivelZoom)
    },
    toggleFibonacci() {
      this.fibonacciAtivo = !this.fibonacciAtivo
      console.log('Fibonacci', this.fibonacciAtivo ? 'ativado' : 'desativado')
    },
    toggleDrawing() {
      this.modoDesenho = !this.modoDesenho
      console.log('Modo desenho', this.modoDesenho ? 'ativado' : 'desativado')
    },
    cancelarOrdem(ordemId) {
      this.minhasOrdens = this.minhasOrdens.filter(ordem => ordem.id !== ordemId)
      console.log('Ordem cancelada:', ordemId)
    }
  }
}
</script>

<style scoped>
/* Gráfico de candles */
.candlestick-chart {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  height: 300px;
  width: 100%;
  margin-top: 10px;
  position: relative;
}

.candle {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 3%;
  position: relative;
}

.wick {
  height: 20px;
  width: 1px;
  background-color: #999;
}

.wick.bottom {
  position: absolute;
  bottom: 0;
  transform: translateY(100%);
}

.body {
  height: 30px;
  width: 100%;
  background-color: #ef5350;
}

.candle-up .body {
  background-color: #26a69a;
}

/* Volume */
.volume-chart {
  display: flex;
  justify-content: space-between;
  height: 80px;
  width: 100%;
}

.volume-bar {
  width: 3%;
  background-color: rgba(239, 83, 80, 0.5);
}

.volume-up {
  background-color: rgba(38, 166, 154, 0.5);
}

/* Indicadores */
.indicator-line {
  position: absolute;
  height: 1px;
  width: 100%;
  z-index: 2;
}

.ma-line {
  top: 40%;
  background-color: #ff9800;
}

.ema-line {
  top: 45%;
  background-color: #9c27b0;
}

.indicator-band {
  position: absolute;
  height: 100px;
  width: 100%;
  z-index: 1;
}

.bb-upper {
  top: 20%;
  border-top: 1px dashed rgba(33, 150, 243, 0.7);
}

.bb-lower {
  top: 60%;
  border-bottom: 1px dashed rgba(33, 150, 243, 0.7);
}

.fibonacci-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to bottom, 
    rgba(255, 152, 0, 0.1) 0%, 
    rgba(255, 152, 0, 0.1) 23.6%, 
    rgba(156, 39, 176, 0.1) 23.6%, 
    rgba(156, 39, 176, 0.1) 38.2%, 
    rgba(33, 150, 243, 0.1) 38.2%, 
    rgba(33, 150, 243, 0.1) 50%, 
    rgba(76, 175, 80, 0.1) 50%, 
    rgba(76, 175, 80, 0.1) 61.8%, 
    rgba(255, 235, 59, 0.1) 61.8%, 
    rgba(255, 235, 59, 0.1) 76.4%, 
    rgba(244, 67, 54, 0.1) 76.4%, 
    rgba(244, 67, 54, 0.1) 100%
  );
  z-index: 1;
}

.price-chart {
  height: 450px;
}

.trading-pair-select {
  max-width: 150px;
}

.chart-toolbar {
  padding: 0 8px;
}

/* Modal para gráfico em tela cheia */
.chart-fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 999;
  background-color: white;
}

/* Responsividade para tamanhos diferentes de tela */
@media (max-width: 960px) {
  .price-chart {
    height: 350px;
  }
  
  .volume-chart {
    height: 60px;
  }
}
</style>
